<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="registration-form__address-info-container">
    <fieldset v-show="formFields.address1 && !formFields.address1.isHidden" 
              id="address1Field"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="Address Line 1"
                    :is-error="checkFieldInvalid(REG_FIELD.ADDRESS1) && isDirty.address1"
                    :is-disabled="!formFields.address1?.isEditable"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.ADDRESS1))"
                    :required="formFields.address1?.required">
        <input id="address1"
               v-model="form.address1"
               type="text"
               placeholder="Enter an address"
               :disabled="!formFields.address1?.isEditable"
               aria-label="Address Line 1"
               :maxlength="formFields.address1?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="address-line1"
               @input="checkResetServerError(REG_FIELD.ADDRESS1)"
               @blur="() => isDirty.address1 = true">
      </UtdTextInput>
    </fieldset>

    <fieldset v-show="formFields.address2 && !formFields.address2.isHidden" 
              id="address2Field"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="Address Line 2"
                    :optional="true"
                    :is-error="false"
                    :is-disabled="!formFields.address2?.isEditable"
                    :required="formFields.address2?.required">
        <input id="address2"
               v-model="form.address2"
               type="text"
               placeholder="Enter an address line 2 (optional)"
               :disabled="!formFields.address2?.isEditable"
               aria-label="Address Line 2"
               :maxlength="formFields.address2?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="address-line2"
               @input="checkResetServerError(REG_FIELD.ADDRESS2)"
               @blur="() => isDirty.address2 = true">
      </UtdTextInput>
    </fieldset>

    <fieldset v-show="formFields.country && !formFields.country.isHidden" 
              id="countryField"
              class="wk-js2 registration-form-field">
      <UtdSelect id="countrySelect"
                 :selected="form.country"
                 :options="countryOptions"
                 label="Country or Region"
                 required
                 placeholder="Select a country or region"
                 @input="emit('selectChange', { value: $event, name: REG_FIELD.COUNTRY})"
      />
    </fieldset>
    
    <fieldset v-show="formFields.zip && !formFields.zip.isHidden && isUSOrCanada" 
              id="zipCodeField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="ZIP/Postal Code"
                    :is-error="checkFieldInvalid(REG_FIELD.ZIP) && isDirty.zip"
                    :is-disabled="!formFields.zip?.isEditable"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.ZIP))"
                    :required="formFields.zip?.required || isUSOrCanada">
        <input id="zipCode"
               v-model="form.zip"
               type="text"
               placeholder="Enter ZIP/Postal code"
               :disabled="!formFields.zip?.isEditable"
               aria-label="ZIP/Postal Code"
               :maxlength="formFields.zip?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="off"
               @input="checkResetServerError(REG_FIELD.ZIP)"
               @blur="processZipCodeBlur">
      </UtdTextInput>
    </fieldset>

    <fieldset v-show="shouldShowCityTextField" 
              id="cityField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="City"
                    :is-error="checkFieldInvalid(REG_FIELD.CITY) && isDirty.city"
                    :is-disabled="!formFields.city?.isEditable"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.CITY))"
                    :required="formFields.city?.required || !isUSA">
        <input id="city"
               v-model="form.city"
               type="text"
               placeholder="Enter a city"
               aria-label="City"
               :disabled="!formFields.city?.isEditable"
               :maxlength="formFields.city?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="address-level2"
               @input="checkResetServerError(REG_FIELD.CITY)"
               @blur="() => isDirty.city = true">
      </UtdTextInput>
    </fieldset>

    <fieldset v-if="shouldShowCityDropdownField" 
              id="cityDropdownField"
              class="wk-js2 registration-form-field">
      <UtdSelect id="cityDropdownSelect"
                 :selected="0"
                 :options="cityDropdownOptions"
                 label="City"
                 required
                 placeholder="Select a city"
                 @input="emit('selectChange', { value: $event, name: REG_FIELD.CITY})"
      />
    </fieldset>

    <fieldset v-if="shouldShowStateField" 
              id="stateField"
              class="wk-js2 registration-form-field">
      <UtdSelect id="stateSelect"
                 :selected="form.state"
                 :value="form.state"
                 :options="filteredStateOptions"
                 label="State"
                 required
                 placeholder="Select a state"
                 @input="emit('selectChange', { value: $event, name: REG_FIELD.STATE })"
      />
    </fieldset>
  </div>
</template>

<script setup>
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdSelect from '_acaSrc/components/shared/input/UtdSelect.vue';
import {
    REG_FIELD,
    REGISTRATION_FIELD_ERROR,
    ZIPCODE_FETCH_SUCCESS
} from '_acaSrc/utility/constants';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import { useStore } from 'vuex';
import { computed } from 'vue';
import zipLocationService from '_acaSrc/composables/zipLocationService.composable';
const store = useStore();
const stateOptions = computed(() => store.getters['account/myAccountStates']);
const shouldShowCityTextField = computed(() => 
    formFields.value.city 
    && !formFields.value.city.isHidden 
    && !isUSA.value);

const shouldShowCityDropdownField = computed(() => 
    formFields.value.city 
    && !formFields.value.city.isHidden 
    && isUSA.value
    && zipCodePrefilled.value);

const registrationForm = computed(() => store.getters['login/registrationForm']);
const formFields = computed(() => registrationForm.value.formFields);
const countryOptions = computed(() => store.getters['account/myAccountCountries']);

const emit = defineEmits([
    'selectChange',
    'close'
]);

const shouldShowStateField = computed(() => {
    return formFields.value.state 
        && !formFields.value.state.isHidden
        && (zipCodePrefilled.value || isCanada.value);
});

const GUEST_PASS_USER_FORM = 'GUEST_SELF_REGISTER';
const processZipCodeBlur = () => {
    isDirty.zip = true;
    if (registrationForm.value.formName === GUEST_PASS_USER_FORM
      && isUSA.value
    ) {
        fetchCityAndState(form.zip);
    }
};

const fetchCityAndState = async(zipCode) => {
    try {
        const {
            status,
            selectedState,
            citiesList,
            county
        } = await zipLocationService().fetchCityStateFromZip(zipCode);
        if (status === ZIPCODE_FETCH_SUCCESS) {
            form.state = getPrefilledStateIndex(selectedState);
            cityDropdownOptions.value = citiesList;
            form.city = '0';
            form.county = county;
            isDirty.state = true;
            isDirty.city = true;
            zipCodePrefilled.value = true;
        }
        else {
            setServerError(REG_FIELD.ZIP, REGISTRATION_FIELD_ERROR.ZIP);
        }
    }
    catch {
        setServerError(REG_FIELD.ZIP, REGISTRATION_FIELD_ERROR.ZIP);
    }
};

const getPrefilledStateIndex = (state) => {
    const selectedIndex = 0;
    stateOptions.value.forEach(option => {
        if (option.value === state) {
            filteredStateOptions.value = [ option ];
        }
    });
    return selectedIndex;
};

const {
    form,
    isDirty,
    isUSOrCanada,
    cityDropdownOptions,
    filteredStateOptions,
    zipCodePrefilled,
    isUSA,
    isCanada,
    checkFieldInvalid,
    getFieldErrorMessage,
    checkResetServerError,
    setServerError
} = useRegistrationForm();
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.registration-form__address-info-container {
  :deep(.utd-phone-input) {
    display: flex;
    flex-wrap: wrap;
    max-width: 320px;
  }

  :deep(.wk-field-input) {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  }
}

#zipCodeField {
  max-width: 260px;
}

.wk-field-is-disabled {
  opacity: 1;

  .wk-field-input.form-field.jumpstart-input {
    background-color: @WKCE-GRAY-TINT5;
  }
}

</style>